import React, {useCallback, useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import Modal from '../../components/Modal';
import {Tabs} from '../../components/Tabs';
import {TypeBadge} from '../../components/Badges';
import StatusIcon from '../../components/StatusIcon';
import TruncatedText from '../../components/TruncatedText';
import DateDisplay from '../../components/DateDisplay';
import DataTable from '../../components/data_table';
import ErrorAlert from '../../components/error_alert';
import {useClientNotificationLookup} from '../../processes/client_subscription_notification';
import {useClientSubscriptionScheduleLookup} from '../../processes/client_subscription_schedule';

export default function NotificationDetailsDialog({subscription, onClose}) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('schedules');
    const shouldFetchSchedules = isDialogOpen && activeTab === 'schedules';
    const shouldFetchNotifications = isDialogOpen && activeTab === 'logs';

    const {
        data: schedules = [],
        isLoading: schedulesLoading,
        error: schedulesError,
        refetch: refetchSchedules,
        isError: schedulesIsError,
        isFetching: schedulesFetching,
    } = useClientSubscriptionScheduleLookup(shouldFetchSchedules ? subscription?.id : null);

    const {
        data: notifications = [],
        isLoading: notificationsLoading,
        error: notificationsError,
        refetch: refetchNotifications,
        isError: notificationsIsError,
        isFetching: notificationsFetching,
    } = useClientNotificationLookup(shouldFetchNotifications ? subscription?.id : null);

    useEffect(() => { if (subscription?.id) setIsDialogOpen(true); }, [subscription?.id]);

    const handleClose = useCallback(() => {
        setIsDialogOpen(false); setActiveTab('schedules'); onClose?.();
    }, [onClose]);

    const handleTabChange = useCallback((tabKey) => setActiveTab(tabKey), []);

    const handleRefresh = useCallback(() => {
        if (activeTab === 'logs') refetchNotifications(); else refetchSchedules();
    }, [activeTab, refetchSchedules, refetchNotifications]);

    const scheduleColumns = useMemo(() => ([
        { id: 'type', header: 'Type', accessorKey: 'type', Cell: ({cell}) => <TypeBadge type={cell.getValue()} /> },
        { id: 'date', header: 'Scheduled Date', accessorKey: 'date', Cell: ({cell}) => <DateDisplay date={cell.getValue()} /> },
    ]), []);

    const notificationColumns = useMemo(() => ([
        { id: 'id', header: 'ID', accessorKey: 'id', size: 80, Cell: ({cell}) => <span className="tw-text-sm">{cell.getValue()}</span> },
        { id: 'email', header: 'Email', accessorKey: 'email', Cell: ({cell}) => <span className="tw-text-sm">{cell.getValue() || 'N/A'}</span> },
        { id: 'name', header: 'Name', accessorKey: 'name', Cell: ({cell}) => <span className="tw-text-sm">{cell.getValue() || 'N/A'}</span> },
        { id: 'type', header: 'Type', accessorKey: 'type', Cell: ({cell}) => <span className="tw-text-sm">{cell.getValue() || 'N/A'}</span> },
        { id: 'sent', header: 'Sent', accessorKey: 'sent', size: 80, Cell: ({cell}) => <StatusIcon status={cell.getValue()} /> },
        { id: 'message', header: 'Message', accessorKey: 'message', Cell: ({cell}) => (<TruncatedText text={cell.getValue()} className="tw-text-sm tw-text-red-600" />) },
        { id: 'createdAt', header: 'Created At', accessorKey: 'createdAt', Cell: ({cell}) => (<DateDisplay date={cell.getValue()} format="YYYY-MM-DD HH:mm:ss" />) },
    ]), []);

    const tabs = useMemo(() => ([{key: 'schedules', label: 'Schedules'}, {key: 'logs', label: 'Logs'}]), []);

    const currentTabData = useMemo(() => {
        if (activeTab === 'logs') {
            return {
                data: notifications,
                columns: notificationColumns,
                isLoading: notificationsLoading || notificationsFetching,
                error: notificationsError,
                isError: notificationsIsError,
                tableClassName: 'tw-w-full',
            };
        }
        return {
            data: schedules,
            columns: scheduleColumns,
            isLoading: schedulesLoading || schedulesFetching,
            error: schedulesError,
            isError: schedulesIsError,
            tableClassName: 'tw-w-1/2 tw-mx-auto',
        };
    }, [activeTab, notifications, notificationColumns, notificationsLoading, notificationsFetching, notificationsError, notificationsIsError, schedules, scheduleColumns, schedulesLoading, schedulesFetching, schedulesError, schedulesIsError]);

    const emptyStateConfig = useMemo(() => ({
        title: `No ${activeTab === 'schedules' ? 'schedule' : 'notification'} records found for this subscription.`,
        subtitle: `Subscription ID: ${subscription?.id}`,
        actionButton: activeTab === 'schedules' ? (
            <button className="tw-btn tw-btn-sm tw-btn-outline" onClick={handleRefresh} disabled={currentTabData.isLoading}>Refresh Schedules</button>
        ) : null,
        loadingMessage: `Loading ${activeTab}...`,
    }), [activeTab, subscription?.id, handleRefresh, currentTabData.isLoading]);

    if (!subscription) return null;

    return (
        <Modal
            isOpen={isDialogOpen}
            onClose={handleClose}
            title={`Notification Details - ${subscription?.client?.name || 'Unknown Client'}`}
            subtitle={`End Date: ${subscription?.end ? moment(subscription.end).format('D MMMM YYYY') : 'N/A'}`}
            size="tw-max-w-7xl"
        >
            <div className="tw-flex tw-flex-col tw-h-full tw-max-h-[70vh]">
                <div className="tw-flex-shrink-0">
                    <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
                    <ErrorAlert error={currentTabData.isError ? currentTabData.error : null} onRetry={handleRefresh} isLoading={currentTabData.isLoading} />
                </div>
                <div className="tw-flex-1 tw-overflow-auto tw-min-h-0">
                    <DataTable
                        data={currentTabData.data}
                        columns={currentTabData.columns}
                        isLoading={currentTabData.isLoading}
                        error={!currentTabData.isError ? null : currentTabData.error}
                        onRetry={handleRefresh}
                        emptyStateConfig={emptyStateConfig}
                        tableClassName={currentTabData.tableClassName}
                    />
                </div>
                <div className="tw-flex tw-items-center tw-pt-4 tw-mt-4 tw-border-t tw-justify-end tw-flex-shrink-0">
                    <button className="tw-btn tw-btn-primary" onClick={handleClose}>Close</button>
                </div>
            </div>
        </Modal>
    );
}
